import type { LotState } from '../hooks/useLotState';
import type { Bidder } from '../data/mockLots';

type Props = {
  state: LotState;
  balanceBids?: number;
  bidPriceUsd?: number;
  winner?: Bidder;
  closedMinutesAgo?: number;
  onBid?: () => void;
  onRemind?: () => void;
};

export function LotMainButton({
  state,
  balanceBids = 87,
  bidPriceUsd = 0.1,
  winner,
  closedMinutesAgo,
  onBid,
  onRemind,
}: Props) {
  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-50 px-3 pb-3 pt-2 bg-gradient-to-t from-bg via-bg/95 to-transparent">
      {state === 'live' && (
        <>
          <button
            type="button"
            onClick={onBid}
            className="relative w-full h-14 group overflow-hidden bg-accent text-bg font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform"
            style={{
              boxShadow: '0 0 26px rgba(255,61,167,0.45), inset 0 0 0 1px rgba(255,255,255,0.14)',
            }}
          >
            <span
              className="absolute inset-0 anim-sweep pointer-events-none"
              style={{
                width: '40%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)',
              }}
            />
            <span className="relative flex items-center justify-between px-5 text-[16px]">
              <span className="flex items-baseline gap-2">
                <span>BID</span>
                <span className="font-mono">${bidPriceUsd.toFixed(2)}</span>
              </span>
              <span className="font-mono text-[11px] opacity-85">+10s timer</span>
            </span>
          </button>
          <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
            your balance: {balanceBids} bids · ≈ ${(balanceBids * bidPriceUsd).toFixed(2)}
          </p>
        </>
      )}

      {state === 'scheduled' && (
        <>
          <button
            type="button"
            onClick={onRemind}
            className="relative w-full h-14 group overflow-hidden border-2 border-primary text-primary font-display font-bold tracking-wider uppercase active:scale-[0.98] transition-transform bg-bg/40 backdrop-blur-sm"
            style={{ boxShadow: '0 0 20px rgba(0,229,255,0.25)' }}
          >
            <span className="relative flex items-center justify-center gap-2 text-[15px]">
              <span>◉</span>
              <span>Remind me</span>
            </span>
          </button>
          <p className="font-mono text-[10px] tracking-wider text-fg-muted mt-2 text-center">
            first bid locks your slot — be early.
          </p>
        </>
      )}

      {state === 'closed' && (
        <div className="w-full h-14 px-4 flex items-center justify-between border border-edge bg-bg-elev/70 text-fg-muted">
          <span className="font-mono text-[10px] tracking-[0.2em]">
            CLOSED{typeof closedMinutesAgo === 'number' ? ` · ${closedMinutesAgo}M AGO` : ''}
          </span>
          {winner && (
            <span className="font-medium text-[12px] text-fg flex items-center gap-1.5">
              <span className="text-success">✓</span>
              {winner.username ? (
                <a
                  href={`https://t.me/${winner.username}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-fg-dim underline-offset-2 hover:text-primary transition-colors"
                >
                  @{winner.username}
                </a>
              ) : (
                <span>{winner.firstName}</span>
              )}
              <span className="text-fg-muted text-[11px]">claimed</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
